import { useMemo, useState, useEffect } from 'react'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getActivityTargets,
    getEmployeeDashboard,
} from '@/modules/Report/services'
import { SalesTargetFilters } from '../../../BusinessTargets/types'
import { Typogaphy, Button } from 'vnetwork-ui'
import { useModal } from '@/contexts/modal'
import { CreateActivityTargetModalProps } from '@/modals/ActivityTargetModals/Create'
import { EditActivityTargetModalProps } from '@/modals/ActivityTargetModals/Edit'
import AddIcons from '@/icons/AddIcons'
import EditIcons from '@/icons/EditIcons'
import { useTranslation } from 'react-i18next'
import Paper from '@/components/Paper'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import type {
    DataTableColumn,
    DataTableAction,
} from '@/components/ui/data-table-customized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import usePermissionsStore from '@/stores/usePermissionsStore'
import useAuthStore from '@/stores/useAuthStore'
import useListTour from './use-list-tour'
import { useTourHandler } from '@/contexts/tour-handler'

interface ListProps {
    filters: SalesTargetFilters
}

interface EmployeeActivityRow {
    employee_id: number
    employee_name: string
    department_name: string | null
    target_presentation: number
    target_poc: number
    target_contract: number
    target_amount: number
    actual_revenue: number
}

export default function List({ filters }: ListProps) {
    const { t } = useTranslation('report')
    const { isAdmin } = usePermissionsStore()
    const { employee } = useAuthStore()

    const { setModal, closeModal } = useModal()
    const [targetYear, setTargetYear] = useState(new Date().getFullYear())

    // Update derived year from filters if available
    useMemo(() => {
        if (filters.gte) {
            const date = new Date(filters.gte)
            setTargetYear(date.getFullYear())
        }
    }, [filters.gte])

    // Open Create Modal
    const handleCreate = () => {
        setModal<CreateActivityTargetModalProps>(
            'ActivityTargetModals/Create/index',
            {
                defaultYear: targetYear,
            }
        )
    }

    // Integrate Tour
    const { startTour } = useListTour({
        onOpenCreateModal: handleCreate,
        onCloseCreateModal: closeModal,
    })
    const { registerHandler, unregisterHandler } = useTourHandler()

    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    // Open Edit Modal
    const handleEdit = (row: EmployeeActivityRow) => {
        setModal<EditActivityTargetModalProps>(
            'ActivityTargetModals/Edit/index',
            {
                editRow: {
                    employee_id: row.employee_id,
                    employee_name: row.employee_name,
                },
                defaultYear: targetYear,
                gte: filters.gte,
                lte: filters.lte,
            }
        )
    }

    // Fetch activity targets + dashboard data concurrently
    const { data: mergedData, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.ACTIVITY_TARGETS,
            'list-merged',
            filters.employee_id,
            filters.gte,
            filters.lte,
        ],
        queryFn: async () => {
            const [activityData, dashboardData] = await Promise.all([
                getActivityTargets(
                    filters,
                    !isAdmin ? employee?.employee_id : undefined
                ),
                getEmployeeDashboard(
                    filters,
                    !isAdmin ? employee?.employee_id : undefined
                ),
            ])

            if (!activityData) return []

            // Map dashboard data by employee_id
            const dashboardMap = new Map<
                number,
                { target_amount: number; actual_revenue: number }
            >()
            dashboardData?.employees?.forEach((emp) => {
                dashboardMap.set(emp.employee_id, {
                    target_amount: emp.target_amount,
                    actual_revenue: emp.actual_revenue,
                })
            })

            return activityData.map((row) => ({
                ...row,
                target_amount:
                    dashboardMap.get(row.employee_id)?.target_amount || 0,
                actual_revenue:
                    dashboardMap.get(row.employee_id)?.actual_revenue || 0,
            }))
        },
        enabled:
            !!filters.employee_id &&
            filters.employee_id.length > 0 &&
            !!filters.gte &&
            !!filters.lte,
    })

    const columns: DataTableColumn<EmployeeActivityRow>[] = useMemo(
        () => [
            {
                key: 'employee_name',
                title: t('employee'),
                width: 200,
                render: (value: string) => (
                    <Typogaphy className="font-medium">{value}</Typogaphy>
                ),
            },
            {
                key: 'department_name',
                title: t('department'),
                width: 150,
                render: (value: string | null) => value || '-',
            },
            {
                key: 'target_presentation',
                title: 'Presentation',
                width: 120,
                align: 'center' as const,
            },
            {
                key: 'target_poc',
                title: 'POC',
                width: 120,
                align: 'center' as const,
            },
            {
                key: 'target_contract',
                title: 'Contract',
                width: 120,
                align: 'center' as const,
            },
            {
                key: 'actual_revenue',
                title: t('target_analytics.actual-revenue'),
                width: 200,
                align: 'right' as const,
                render: (value: number) =>
                    value ? value.toLocaleString() : '-',
            },
            {
                key: 'target_amount',
                title: t('target_analytics.target-amount'),
                width: 200,
                align: 'right' as const,
                render: (value: number) =>
                    value ? value.toLocaleString() : '-',
            },
        ],
        [t]
    )

    const actions: DataTableAction<EmployeeActivityRow>[] = useMemo(
        () =>
            isAdmin
                ? [
                      {
                          key: 'edit',
                          label: t('target_analytics.edit-kpi'),
                          icon: <EditIcons />,
                          onClick: (row) => handleEdit(row),
                      },
                  ]
                : [],
        [isAdmin, t, handleEdit]
    )

    const stateComponent = renderState()

    return (
        <div
            className="flex flex-col"
            data-tour="target-analytics-list-container"
        >
            <Paper className="flex flex-col gap-4">
                {/* Header with create button */}
                {isAdmin && (
                    <div className="flex items-center justify-end">
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcons />}
                            onClick={handleCreate}
                            data-tour="target-analytics-create-btn"
                        >
                            {t('target_analytics.create-kpi')}
                        </Button>
                    </div>
                )}

                {stateComponent || (
                    <DataTableCustomized<EmployeeActivityRow>
                        columns={columns}
                        rows={mergedData ?? []}
                        rowKey={(row) => row.employee_id}
                        actions={actions.length > 0 ? actions : undefined}
                        size="small"
                        sorting={{
                            defaultSortKey: 'department_name',
                            defaultSortOrder: 'asc',
                        }}
                    />
                )}
            </Paper>
        </div>
    )
}
